import { Icon } from '../ui/Icon';
import { Button, Card, EmptyPanel } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { EXPENSES_EMPTY_TEXT, EXPENSES_EMPTY_TITLE, PURCHASES_NOTE, type MoneyReportView } from '../../utils/reportsView';
import { Breakable, DeltaText, ReportsMoney, ReportsNotes, ReportsSay, ReportsTile } from './ReportsParts';

/**
 * Rediseño · Bloque F — R2 · «¿Dónde se va mi dinero?». P-030 no lo dibuja en detalle (es una tarjeta
 * más del prototipo); su contenido es el que ya tenía Reportes (P-032, I3: AS-IS) —gastos confirmados por
 * categoría con su cambio y las compras de insumos aparte, con la razón escrita— con el lenguaje visual
 * de R1. Solo dibuja `MoneyReportView`.
 */
export function ReportMoney({ view, onNavigate, canOpenExpenses }: { view: MoneyReportView; onNavigate: (destination: string) => void; canOpenExpenses: boolean }) {
  return (
    <>
      {view.say && <ReportsSay segments={view.say} />}

      <Card aria-labelledby="reports-expenses-title" as="section" className="n3-reports__money-card">
        <h2 className="n3-reports__card-title" id="reports-expenses-title">
          <ReportsTile icon="wallet" size="sm" tone="danger" />
          <span>Gastos confirmados</span>
        </h2>
        {view.rows.length === 0
          ? <EmptyPanel description={EXPENSES_EMPTY_TEXT} plain title={EXPENSES_EMPTY_TITLE} />
          : (
            <>
              <p className="n3-reports__total"><ReportsMoney label={view.totalLabel} /></p>
              {view.totalDelta && <DeltaText className="n3-reports__total-delta" delta={view.totalDelta} />}
              <ul className="n3-reports__categories">
                {view.rows.map((row) => (
                  <li className="n3-reports__category" key={row.name}>
                    <span className="n3-reports__category-name">{row.name}</span>
                    <span className="n3-reports__category-amount"><ReportsMoney label={row.amountLabel} /></span>
                    <span aria-hidden="true" className="n3-reports__category-track">
                      {row.barPercent > 0 && <span className="n3-reports__category-bar" style={{ width: `${row.barPercent}%` }} />}
                    </span>
                    <span className={cx('n3-reports__category-change', `n3-reports__category-change--${row.changeDirection}`)}>
                      {row.changeDirection !== 'flat' && <span aria-hidden="true" className="n3-reports__arrow">{row.changeDirection === 'up' ? '▲' : '▼'}</span>}
                      <span><Breakable text={row.changeLabel} /></span>
                    </span>
                  </li>
                ))}
              </ul>
              {view.comparison && <p className="n3-reports__comparison">{view.comparison}</p>}
            </>
          )}
        {canOpenExpenses && (
          <Button className="n3-reports__link" iconEnd={<Icon name="chevron-right" size={16} />} onClick={() => onNavigate('expenses')} variant="ghost">Ver mis gastos</Button>
        )}
      </Card>

      <Card aria-labelledby="reports-purchases-title" as="section" className="n3-reports__money-card">
        <h2 className="n3-reports__card-title" id="reports-purchases-title">
          <ReportsTile icon="purchases" size="sm" tone="warning" />
          <span>Compras de insumos confirmadas</span>
        </h2>
        <p className="n3-reports__total"><ReportsMoney label={view.purchasesLabel} /></p>
        <p className="n3-reports__panel-note">{PURCHASES_NOTE}</p>
      </Card>

      <ReportsNotes notes={[view.disclaimer]} />
    </>
  );
}
